import * as React from 'react';
import { IPremium, IProduct, ITraveller } from '../../../../../../data/api/models';
import { find, flow, get, map } from 'lodash';
import { IQuoteActions } from 'src/app/services/quote/models';
import { IBrandingActions } from 'src/app/services/branding/models';
import { formValueSelector, getFormSyncErrors, InjectedFormProps, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { IStore } from '../../../../../../redux/IStore';
import { RenewalSubmitButton } from '../RenewalSubmitButton';
import { MTACardHeader } from '../../../MakeAChange/components/CoverDetails/scenes/Components/MTACardHeader';
import { OptionsYesSelectionText } from '../../../MakeAChange/components/CoverDetails/scenes/Components/OptionsYesSelectionText';
import OptionWithPopup from '../../../../../Quote/scenes/AdditionalCover/components/QuestionOptions/OptionWithPopup';
import OptionsModal from '../../../MakeAChange/components/CoverDetails/scenes/Components/OptionsModal';
import { differenceInYears } from 'date-fns';

interface ChangeAdditionalOptionsProps extends InjectedFormProps {
  product: IProduct;
  change: any;
  isLoading: boolean;
  schemeId: string;
  options: any;
  dispatch: any;
  premium: IPremium[];
  travellers: ITraveller[];
  visibility: { options: any }
  actions: {
    quote: IQuoteActions;
    branding: IBrandingActions;
  };
  goBackButton: any;
}


class ChangeAdditionalOptions extends React.Component<ChangeAdditionalOptionsProps, any> {

  constructor(props) {
    super(props);

    this.state = {
      isLoading: false,
      hasWinterSportsExclusion: false,
      error: '',
    };
  }

  public componentDidMount() {
    const { options, dispatch, change, actions } = this.props;

    // Set the default values from the options
    const allOptions = ['golf_cover', 'winter_sports', 'cruise_cover', 'gadget_cover', 'excess_waiver'];
    allOptions.forEach((key) => {
      if (!options[key] || options[key] === undefined) {
        dispatch(change(`options.${key}`, false));
        dispatch(change(`visibility.options.${key}`, false));
      } else if (options[key]) {
        dispatch(change(`visibility.options.${key}`, true));
      }
    });
    this.setState({ isLoading: true })
    Promise.resolve(actions.quote.checkIfHasWSExclusion()).then((data) => {
      this.setState({ isLoading: false, hasWinterSportsExclusion: data || false })
    }).catch(() => {
      this.setState({ isLoading: false })
    })

  }

  private recalculatePrice = () => {
    const { actions, product } = this.props;
    setTimeout((() => {
      actions.quote.calculateQuote(product.id, true, true);
    }), 300);
  }

  private unselectAdd = (key) => {
    const { options } = this.props;
    if (options[key]) {
      this.props.change(`options.${key}`, false);
      this.recalculatePrice();
    }
  }

  private renderOption = (option, key) => {
    const {
      product,
      visibility,
      travellers,
      premium,
      schemeId,
      isLoading,
    } = this.props;

    const { hasWinterSportsExclusion } = this.state;

    if (key === 'winter_sports' && hasWinterSportsExclusion) {
      return;
    }

    if (!['cruise_cover', 'winter_sports', 'gadget_cover', 'excess_waiver', 'enhanced_trip_disruption'].includes(key)) {
      return;
    }

    const leadTraveller = travellers[0];
    const leadTravellerAge = differenceInYears(new Date(), leadTraveller.dob);
    if (leadTravellerAge > 65 && key === 'winter_sports') {
      return null;
    }

    let dynamicText = product.metadata.definitions[`${key}_alt_dynamic`];
    let title;
    const selection = find(premium, ['scheme.id', schemeId]);
    const boltOns = get(selection, 'information.boltOns', null);
    const boltOn = get(boltOns, key, null);
    const boltOnTotal = get(boltOn, 'total', 0);
    const currency = get(selection, 'premium.currency', 'GBP');

    if (!boltOnTotal) {
      return;
    }

    let yesSelection;
    let selectedNoText;

    if (key === 'gadget_cover') {
      title = 'Gadget Cover'
      yesSelection = [
        'Up to a maximum of £1,500 per trip for the loss, theft or damage of gadgets owned by yourself.',
        'Single item limit is up to £1,500',
        <>
          <div>
            <div>Refurbished gadgets must be bought directly from the manufacturer or a reputable retailer. For second
              hand items, you must be able to provide proof of purchase such as a printed or digital receipt.
            </div>
            <div style={{ marginTop: '10px' }}>Important: Gadgets are subject to wear and tear deductions. This doesn’t
              apply to
              gadgets less than 12 months old at the time of the incident. 10% per year up to a maximum depreciation of
              60%.
            </div>
            <div style={{ marginTop: '10px' }}>For example, if you bought an item for £200 three years ago, and the rate
              of
              depreciation is considered to be 10% a year, the deduction would be calculated as 30% (10% x 3 years) of
              the
              original £200.
            </div>
          </div>
        </>,
      ]
    }

    if (key === 'excess_waiver') {
      title = 'Excess Waiver'
      yesSelection = [
        'Excess waiver cover removes all excess amounts that are payable at the point a claim is made unless the reason for the claim occurred before the cover was active on your policy.',
      ]
    }

    if (key === 'winter_sports') {
      title = 'Winter Sports'
      yesSelection = [
        'Up to £500 for winter sports related incidents',
        'Lost, stolen or damaged equipment owned or hired by yourself',
        'Piste closure and delay caused by avalanche',
        'Winter Sports trips are covered up to a maximum of 21 days per policy term'
      ]

      selectedNoText = (
        <div>IMPORTANT - This policy will not cover claims for medical treatment, cancellation or cutting your trip
          short if you take part in winter sports activities.
        </div>
      )
    }

    if (key === 'cruise_cover') {
      title = 'Cruise Cover'
      yesSelection = [
        'Up to £750 for Cruise related incidents',
        'Missed port departure, cruise interruption and itinerary change',
        'Extend your policy limits to cover you whilst on a cruise',
      ]

      selectedNoText = (
        <div>IMPORTANT - This policy will not cover claims for medical treatment, cancellation or cutting your trip
          short if you plan on going on a cruise.
        </div>
      )
    }

    if (key === 'enhanced_trip_disruption') {
      title = 'Enhanced Trip Disruption Cover'
      yesSelection = [
        'Cover for the delay benefit with the minimum delay period to qualify for cover reduced from 12 hours to 6 hours',
        'Cover for onward expenses incurred as the result of a delay, such as additional transfer/car hire costs as well as cover for expenses for unused excursions ',
        'Cover for disruption caused by airspace closure, air traffic control restrictions, mid-air emergencies, and technical issues',
        'Cover for connecting flights (outbound and return journeys only)',
      ]

      selectedNoText = (
        <div>
          <b>Please note - </b>
          The enhanced trip disruption cover does not cover mid-trip connections or unused accommodation for delays of less than 24 hours
        </div>
      )
    }

    dynamicText = (<OptionsYesSelectionText text={yesSelection}/>)

    return (
      <>
        <div className="admiral-card" style={{margin: 'auto auto 15px auto'}}>
          <div className="body" style={{padding: '15px'}}>
            <div className="medical-section-group options-section-group" key={key}>

              <OptionWithPopup title={title}
                               name={`options.${key}`}
                               visibilityName={`options.${key}`}
                               selectedYesText={dynamicText}
                               selectedNoText={selectedNoText}
                               optionKeyName={key}
                               onOptionClick={this.recalculatePrice}
                               disabledCase={isLoading}
                               currency={currency}
                               boltOnTotal={boltOnTotal}
                               active={visibility && visibility?.options[`${key}`]}
                               visibilityOfAddButton={visibility && visibility.options[key]}
                               props={this.props}
                               unselectAdd={this.unselectAdd.bind(this, key)}/>
            </div>
          </div>
        </div>
      </>
    );
  }

  render() {

    const { product, isLoading, goBackButton } = this.props;

    return (
      <div className="page-container">
        {this.state.error && (
          <div className="error-block-container">
            <h1>Whoops!</h1>
            <p>{this.state.error}</p>
          </div>
        )}

        <div>
          <div style={{margin: '20px auto 20px auto'}}>
            <MTACardHeader
              text={'Personalise your cover below.'}
              subtext={'Please answer the following questions to make sure your cover meets your needs. '}
              icon={'insurance'}
            />
          </div>

          <div>
            <div>
              {map(product.metadata.options, this.renderOption)}
            </div>
          </div>

          <OptionsModal/>

          <div className={'renewal-edit-form-buttons'} style={{marginTop: '20px'}}>
            <div className={'renewal-go-back-button'}>
              {goBackButton}
            </div>
            <div className={'renewal-submit-button'}>
              <RenewalSubmitButton isLoading={isLoading}/>
            </div>
          </div>

        </div>

      </div>
    );
  }
}

let selector;

export default flow([
  reduxForm({
    form: 'quote',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  }),
  (component) => {
    selector = formValueSelector('quote');

    return component;
  },
  connect(
    (state: IStore) => {
      const quote = state.quote;
      const options = selector(state, 'options');
      const visibility = selector(state, 'visibility');
      const tripType = selector(state, 'tripType');
      const schemeId = selector(state, 'schemeId');
      const startDate = selector(state, 'startDate');
      const travellers = selector(state, 'travellers');
      const endDate = selector(state, 'endDate');
      const premium = selector(state, 'premiums');
      const channel = state.branding.channel;
      const synchronousError = getFormSyncErrors('quote')(state);

      return {
        options,
        quote,
        schemeId,
        startDate,
        endDate,
        channel,
        visibility,
        tripType,
        premium,
        synchronousError,
        travellers,
      };
    },
  ),
])(ChangeAdditionalOptions);

